import { memo, useMemo } from "react";
import { typography, transitions } from "../tokens";

const PAGE_SIZE = 8;

export function usePagination<T>(items: T[], page: number) {
  return useMemo(() => {
    const totalPages = Math.max(1, Math.ceil(items.length / PAGE_SIZE));
    const safePage = Math.min(page, totalPages);
    const start = (safePage - 1) * PAGE_SIZE;
    const paginatedItems = items.slice(start, start + PAGE_SIZE);
    return { paginatedItems, totalPages, currentPage: safePage, totalItems: items.length };
  }, [items, page]);
}

export const Pagination = memo(function Pagination({
  currentPage,
  totalPages,
  totalItems,
  onPageChange,
}: {
  currentPage: number;
  totalPages: number;
  totalItems: number;
  onPageChange: (page: number) => void;
}) {
  if (totalPages <= 1) return null;

  const start = (currentPage - 1) * PAGE_SIZE + 1;
  const end = Math.min(currentPage * PAGE_SIZE, totalItems);

  const pages = useMemo(() => {
    const result: (number | "...")[] = [];
    if (totalPages <= 7) {
      for (let i = 1; i <= totalPages; i++) result.push(i);
    } else {
      result.push(1);
      if (currentPage > 3) result.push("...");
      const rangeStart = Math.max(2, currentPage - 1);
      const rangeEnd = Math.min(totalPages - 1, currentPage + 1);
      for (let i = rangeStart; i <= rangeEnd; i++) result.push(i);
      if (currentPage < totalPages - 2) result.push("...");
      result.push(totalPages);
    }
    return result;
  }, [currentPage, totalPages]);

  return (
    <div
      style={{
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
        padding: "12px 0 0",
        borderTop: "1px solid #f2f2f2",
        marginTop: "8px",
      }}
    >
      <span
        style={{
          fontFamily: typography.fontFamily,
          fontSize: typography.size.xs,
          color: "#aaa",
        }}
      >
        {start}–{end} of {totalItems}
      </span>

      <div style={{ display: "flex", alignItems: "center", gap: "4px" }}>
        <NavBtn
          label="‹"
          disabled={currentPage === 1}
          onClick={() => onPageChange(currentPage - 1)}
        />
        {pages.map((p, i) =>
          p === "..." ? (
            <span
              key={`dots-${i}`}
              style={{
                fontFamily: typography.fontFamily,
                fontSize: typography.size.xs,
                color: "#ccc",
                padding: "0 4px",
                userSelect: "none",
              }}
            >
              ...
            </span>
          ) : (
            <PageBtn
              key={p}
              page={p}
              active={p === currentPage}
              onClick={() => onPageChange(p)}
            />
          )
        )}
        <NavBtn
          label="›"
          disabled={currentPage === totalPages}
          onClick={() => onPageChange(currentPage + 1)}
        />
      </div>
    </div>
  );
});

const PageBtn = memo(function PageBtn({
  page,
  active,
  onClick,
}: {
  page: number;
  active: boolean;
  onClick: () => void;
}) {
  return (
    <button
      onClick={onClick}
      aria-current={active ? "page" : undefined}
      style={{
        width: "30px",
        height: "30px",
        borderRadius: "8px",
        border: "none",
        background: active ? "#111" : "transparent",
        color: active ? "#fff" : "#666",
        fontFamily: typography.fontFamily,
        fontSize: typography.size.xs,
        fontWeight: active ? typography.weight.bold : typography.weight.medium,
        cursor: "pointer",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        transition: `all ${transitions.fast}`,
      }}
      onMouseEnter={e => {
        if (!active) (e.currentTarget as HTMLButtonElement).style.background = "#f4f4f4";
      }}
      onMouseLeave={e => {
        if (!active) (e.currentTarget as HTMLButtonElement).style.background = "transparent";
      }}
    >
      {page}
    </button>
  );
});

function NavBtn({ label, disabled, onClick }: { label: string; disabled: boolean; onClick: () => void }) {
  return (
    <button
      onClick={onClick}
      disabled={disabled}
      aria-label={label === "‹" ? "Previous page" : "Next page"}
      style={{
        width: "30px",
        height: "30px",
        borderRadius: "8px",
        border: "1px solid #e8e8e8",
        background: "#fff",
        color: disabled ? "#ddd" : "#555",
        fontFamily: typography.fontFamily,
        fontSize: "16px",
        cursor: disabled ? "default" : "pointer",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        opacity: disabled ? 0.5 : 1,
        transition: `all ${transitions.fast}`,
      }}
      onMouseEnter={e => {
        if (!disabled) {
          const el = e.currentTarget as HTMLButtonElement;
          el.style.borderColor = "#111";
          el.style.color = "#111";
        }
      }}
      onMouseLeave={e => {
        if (!disabled) {
          const el = e.currentTarget as HTMLButtonElement;
          el.style.borderColor = "#e8e8e8";
          el.style.color = "#555";
        }
      }}
    >
      {label}
    </button>
  );
}
